// AuthDebug.js
import baseURL from "../config/config";
// Temporary device/authentication diagnostics.
// Enable with: /login?debug=1
//
// IMPORTANT: Never log passwords, access tokens, refresh tokens, or full API responses.

const DEBUG_ENDPOINT = `${baseURL}/api/debug/auth-events/`; // TODO: replace with real backend endpoint.

let debugEnabled = false;
let debugSessionId = null;
let eventQueue = [];
let flushTimer = null;
const onceKeys = new Set();

function getDebugEnabled() {
  try {
    const debugValue = new URLSearchParams(window.location.search).get("debug");

    if (debugValue === "0" || debugValue === "off") {
      try {
        sessionStorage.removeItem("praccel_debug_mode");
        sessionStorage.removeItem("praccel_debug_session_id");
      } catch {
        // Ignore storage cleanup failures.
      }
      return false;
    }

    const queryEnabled = debugValue === "1";

    if (queryEnabled) {
      try {
        sessionStorage.setItem("praccel_debug_mode", "1");
      } catch {
        // Continue even if sessionStorage is unavailable.
      }
      return true;
    }

    return sessionStorage.getItem("praccel_debug_mode") === "1";
  } catch {
    return false;
  }
}

debugEnabled = getDebugEnabled();

function createSessionId() {
  const random =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `dbg-${random}`;
}

function getSessionId() {
  if (!debugEnabled) return null;
  if (debugSessionId) return debugSessionId;

  try {
    const key = "praccel_debug_session_id";
    debugSessionId = sessionStorage.getItem(key);

    if (!debugSessionId) {
      debugSessionId = createSessionId();
      sessionStorage.setItem(key, debugSessionId);
    }
  } catch {
    debugSessionId = createSessionId();
  }

  return debugSessionId;
}

function safeStorageTest(storage) {
  try {
    const key = `__praccel_debug_${Date.now()}`;
    storage.setItem(key, "ok");
    const readBack = storage.getItem(key);
    storage.removeItem(key);

    return {
      available: true,
      writeRead: readBack === "ok",
    };
  } catch (error) {
    return {
      available: false,
      writeRead: false,
      error: error?.name || "StorageError",
    };
  }
}

export function isAuthDebugEnabled() {
  return debugEnabled;
}

export function getDebugSessionId() {
  return getSessionId();
}

export function getDeviceInfo() {
  if (!debugEnabled) return {};

  let timezone = "unknown";

  try {
    timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
  } catch {
    // Ignore unsupported/old browser behavior.
  }

  return {
    userAgent: navigator.userAgent || "unknown",
    platform: navigator.platform || "unknown",
    language: navigator.language || "unknown",
    timezone,
    deviceTime: new Date().toString(),
    deviceTimeISO: new Date().toISOString(),
    screenWidth: window.screen?.width ?? null,
    screenHeight: window.screen?.height ?? null,
    viewportWidth: window.innerWidth ?? null,
    viewportHeight: window.innerHeight ?? null,
    devicePixelRatio: window.devicePixelRatio ?? null,
    online: navigator.onLine,
  };
}

export function getStorageInfo() {
  if (!debugEnabled) return {};

  return {
    localStorage: safeStorageTest(window.localStorage),
    sessionStorage: safeStorageTest(window.sessionStorage),
  };
}

export function getAuthStorageSnapshot() {
  if (!debugEnabled) return {};

  const exists = (key) => {
    try {
      return {
        exists: window.localStorage.getItem(key) !== null,
        empty: window.localStorage.getItem(key) === "",
      };
    } catch (error) {
      return {
        exists: false,
        empty: false,
        error: error?.name || "StorageError",
      };
    }
  };

  return {
    accessToken: exists("access_token"),
    refreshToken: exists("refresh_token"),
    accessExpiry: exists("access_expiry"),
    refreshExpiry: exists("refresh_token_expiration"),
    userType: exists("user_type"),
    userData: exists("user_data"),
    features: exists("features"),
    settings: exists("settings"),
  };
}

async function flushDebugEvents() {
  if (!debugEnabled || eventQueue.length === 0) return;

  const events = eventQueue.splice(0, eventQueue.length);

  const payload = JSON.stringify({
    session_id: getSessionId(),
    events,
  });

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], {
        type: "application/json",
      });

      const accepted = navigator.sendBeacon(DEBUG_ENDPOINT, blob);

      if (accepted) return;
    }

    await fetch(DEBUG_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: payload,
      keepalive: true,
    });
  } catch (error) {
    // Debugging must never break the application.
    console.warn("[AuthDebug] Could not send diagnostics:", error);
  }
}

function scheduleFlush() {
  if (!debugEnabled || flushTimer) return;

  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushDebugEvents();
  }, 500);
}

export function debugEvent(event, data = {}, options = {}) {
  if (!debugEnabled) return;

  const entry = {
    event,
    timestamp: new Date().toISOString(),
    pathname: window.location.pathname,
    search: window.location.search,
    ...data,
  };

  eventQueue.push(entry);

  // Console output is useful if DevTools is ever available.
  console.log("[PRACCEL AUTH DEBUG]", entry);

  if (options.flush === true) {
    void flushDebugEvents();
  } else {
    scheduleFlush();
  }
}

export function debugEventOnce(key, event, data = {}) {
  if (!debugEnabled || onceKeys.has(key)) return;

  onceKeys.add(key);
  debugEvent(event, data);
}

export function debugNavigation(target) {
  debugEvent("NAVIGATION", {
    from: window.location.pathname,
    target,
  });
}

export function debugError(event, error, extra = {}) {
  if (!debugEnabled) return;

  debugEvent(event, {
    ...extra,
    errorName: error?.name || "UnknownError",
    errorMessage: error?.message || String(error),
    httpStatus: error?.response?.status ?? null,
  });
}

// Flush pending events when the page is backgrounded/closed.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    void flushDebugEvents();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushDebugEvents();
    }
  });
}
